import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Table } from 'primeng/table';
import {
  SimplifiedTableColumnDef,
  SimplifiedTableColumnDefFlagFn,
  SimplifiedTableColumnDefTextSub,
  SimplifiedTableColumnDefUser,
  SimplifiedTableColumnFlagEntryExpression,
  SimplifiedTableColumnFlagOptionEntry,
  SimplifiedTableRowAction,
  SimplifiedTableRowActionEvent,
  SimplifiedTableSettings,
} from '../../types';
import { find, get } from 'lodash';
import { getAvatarColor, getAvatarInitials } from '../../../utils/avatar';
import {
  getRandomUID,
  simpleStringHash,
  translateArrayProperty,
} from '../../../utils';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'simplified-table',
  templateUrl: './simplified-table.component.html',
  styleUrl: './simplified-table.component.scss',
  standalone: false,
  encapsulation: ViewEncapsulation.None,
})
export class SimplifiedTableComponent implements OnInit, OnChanges {
  @HostBinding('class')
  readonly cssClass = 'simplified-table ';

  afterInitialized = false;
  searchValue = '';
  tableId = getRandomUID(false, true);

  @ViewChild('dt') dt?: Table;

  @Input() tableSettings: SimplifiedTableSettings = {
    showGlobalSearch: true,
    showClear: true,
    showRefresh: true,
    selectableRows: true,
    pageSize: 10,
    dataKey: 'uid',
    emptyMessage: 'NoItemsFound',
    rowActions: [],
    tableActions: [],
  };

  @Input() columnDefs: SimplifiedTableColumnDef[] = [];
  @Input() dataItems: any[] = [];
  @Input() dataItemsSelected: any[] = [];

  @Input() isLoading = false;

  @HostBinding('class.card-dashboard')
  @Input()
  isCard = true;

  @Output() rowAction = new EventEmitter<SimplifiedTableRowActionEvent>();
  @Output() tableAction = new EventEmitter<string>();

  columnFilterModelsById: Record<string, string[]> = {};
  columnFilterOptionsById: Record<string, any[]> = {};

  globalFilterFields: string[] = []; //['name','country.name','representative.name','status'];

  getColumnFilterId(columnName: string): string {
    return `${this.tableId}-${simpleStringHash(columnName)}`;
  }

  getFeaturedFilterStr(columnDef: SimplifiedTableColumnDef): string {
    const columnFilterId = this.getColumnFilterId(columnDef.column || '');
    if (this.columnFilterModelsById[columnFilterId].length === 0) {
      return this.translateService.instant(
        columnDef.featuredFilter?.defaultLabelSummary || '',
      );
    } else {
      const translatedValues: string[] = [];
      (this.columnFilterModelsById[columnFilterId] || []).forEach(
        (thisFilterValue) => {
          const info = find(columnDef.featuredFilter?.options, {
            value: thisFilterValue,
          });
          if (info) {
            translatedValues.push(
              this.translateService.instant(info.labelSummary),
            );
          }
        },
      );
      return translatedValues.join(', ');
    }
  }

  testClick($event: any, columnName: string): void {
    console.log($event);
    console.log($event.target);

    document.getElementById(this.getColumnFilterId(columnName))?.click();
  }

  constructor(public translateService: TranslateService) {}
  ngOnInit() {}

  ngOnChanges(): void {
    this.initTable();

    console.log(this.globalFilterFields);
  }

  initTable(): void {
    this.globalFilterFields = [];
    this.columnFilterModelsById = {};
    this.columnFilterOptionsById = {};
    this.columnDefs.forEach((columnDef) => {
      if (columnDef.featuredFilter) {
        const thisColumnFeatureId = this.getColumnFilterId(
          columnDef.column || '',
        );
        this.columnFilterModelsById[thisColumnFeatureId] = [];
        /*(columnDef.featuredFilter.options || []).forEach((option) => {
          if (option.defaultSelected) {
            this.columnFilterModelsById[thisColumnFeatureId].push(option.value);
          }
        });*/

        // console.log(this.dt);
        /*if (this.dt) {
          this.dt.filter(
            this.columnFilterModelsById[thisColumnFeatureId],
            columnDef.column || '',
            'in',
          );
        }*/

        this.columnFilterOptionsById[thisColumnFeatureId] =
          translateArrayProperty(
            columnDef.featuredFilter.options,
            'label',
            this.translateService,
          );
      }
      if (columnDef.globalFilter) {
        const useFilterColumn =
          columnDef.filtering?.overrideColumn ?? columnDef.column;
        if (useFilterColumn) {
          this.globalFilterFields.push(useFilterColumn);
        }

        switch (columnDef.type) {
          case 'text-sub':
            this.globalFilterFields.push(columnDef.columnSub);
            break;
          case 'user':
            if (columnDef.email) {
              this.globalFilterFields.push(columnDef.email);
            }
            if (columnDef.emailAlternative) {
              this.globalFilterFields.push(columnDef.emailAlternative);
            }
            if (columnDef.userType) {
              this.globalFilterFields.push(columnDef.userType);
            }
            break;
        }
      }
    });
  }

  getEventValue(event: any): string {
    return event?.target?.value ?? '';
  }

  clear(table: Table) {
    console.log('clear');
    table.clear();

    // table.clearFilterValues();
    this.searchValue = '';
    this.columnDefs.forEach((columnDef) => {
      if (columnDef.featuredFilter) {
        const thisColumnFeatureId = this.getColumnFilterId(
          columnDef.column || '',
        );
        this.columnFilterModelsById[thisColumnFeatureId] = [];
      }
    });
  }

  refresh() {
    this.tableAction.emit('refresh');
  }
  onTableAction(actionId: string): void {
    this.tableAction.emit(actionId);
  }

  getTotalTdCount(): number {
    let toReturn = this.columnDefs.length;
    if (this.tableSettings?.rowActions?.length) {
      toReturn++;
    }
    if (this.tableSettings.selectableRows) {
      toReturn++;
    }
    return toReturn;
  }

  getColumnValueFlagOption(
    dataItem: any,
    columnDef: SimplifiedTableColumnDef,
  ): SimplifiedTableColumnFlagOptionEntry | undefined {
    const columnDefFlagFn = columnDef as SimplifiedTableColumnDefFlagFn;
    const value = columnDefFlagFn.valueFn
      ? columnDefFlagFn.valueFn(dataItem)
      : this.getColumnValue(dataItem, columnDef.column);
    return find(columnDefFlagFn.options, { value: value });
  }
  getColumnValue(
    dataItem: any,
    column?: string,
    expression?: SimplifiedTableColumnFlagEntryExpression,
  ): string {
    if (!column) {
      return '';
    }
    const isNegative = column.indexOf('!') === 0;
    let value = get(dataItem, isNegative ? column.substr(1) : column);
    if (expression === 'hasItems') {
      value = value && value.length;
    } else if (expression === 'alwaysTrue') {
      value = true;
    }
    return isNegative ? !value : value;
  }

  getUserEmailColumnValue(
    dataItem: any,
    columnDef: SimplifiedTableColumnDef,
  ): string {
    const userColumnDef = columnDef as SimplifiedTableColumnDefUser;
    return this.getColumnValue(dataItem, userColumnDef.email) ||
      userColumnDef?.emailAlternative
      ? this.getColumnValue(dataItem, userColumnDef.emailAlternative as string)
      : '';
  }

  getUserAvatarInitials(fullname?: string, email?: string): string {
    const fullNameSplited = (fullname || '').split(' ');
    return getAvatarInitials(
      fullNameSplited.length >= 1 && fullNameSplited[0].length >= 1
        ? fullNameSplited[0].charAt(0)
        : undefined,
      fullNameSplited.length >= 2 && fullNameSplited[1].length >= 1
        ? fullNameSplited[1].charAt(0)
        : undefined,
      email,
    );
  }

  getUserAvatarColor(fullname?: string, email?: string): string {
    return getAvatarColor(fullname, '', email);
  }

  showRowAction(dataItem: any, rowAction: SimplifiedTableRowAction): boolean {
    if (!rowAction.showIf) {
      return true;
    }

    return rowAction.showIf(dataItem);
  }
  onRowAction(dataItem: any, rowActionId: string): void {
    console.log(dataItem);
    console.log(rowActionId);
    this.rowAction.emit({ dataItem, actionId: rowActionId });
  }

  /*getSeverity(status: string) {
    switch (status) {
      case 'unqualified':
        return 'danger';

      case 'qualified':
        return 'success';

      case 'new':
        return 'info';

      case 'negotiation':
        return 'warn';

      case 'renewal':
        return 'info';
    }
    return 'info';
  }*/

  /*this.representatives = [
      { name: 'Amy Elsner', image: 'amyelsner.png' },
      { name: 'Anna Fali', image: 'annafali.png' },
      { name: 'Asiya Javayant', image: 'asiyajavayant.png' },
      { name: 'Bernardo Dominic', image: 'bernardodominic.png' },
      { name: 'Elwin Sharvill', image: 'elwinsharvill.png' },
      { name: 'Ioni Bowcher', image: 'ionibowcher.png' },
      { name: 'Ivan Magalhaes', image: 'ivanmagalhaes.png' },
      { name: 'Onyama Limba', image: 'onyamalimba.png' },
      { name: 'Stephen Shaw', image: 'stephenshaw.png' },
      { name: 'Xuxue Feng', image: 'xuxuefeng.png' },
    ];

    this.statuses = [
      { label: 'Unqualified', value: 'unqualified' },
      { label: 'Qualified', value: 'qualified' },
      { label: 'New', value: 'new' },
      { label: 'Negotiation', value: 'negotiation' },
      { label: 'Renewal', value: 'renewal' },
      { label: 'Proposal', value: 'proposal' },
    ];

    this.customers = [
      {
        id: 1000,
        name: 'James Butt',
        country: {
          name: 'Algeria',
          code: 'dz',
        },
        company: 'Benton, John B Jr',
        date: '2015-09-13',
        status: 'unqualified',
        verified: true,
        activity: 17,
        representative: {
          name: 'Ioni Bowcher',
          image: 'ionibowcher.png',
        },
        balance: 70663,
      },
    ];*/
}
